import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { ChevronRight, ChevronDown, Edit, Trash2, Plus, Save, X, GripVertical, BookOpen, Target, Link, Printer, FileText, Download } from 'lucide-react'
import HelpButton from '../components/HelpButton'
import { CurriculumPrintView } from '../components/CurriculumPrintView'
import { generatePDF, sanitizeFilename } from '../utils/pdfGenerator'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Competency {
  id: number
  name: string
  description?: string
  parent_id?: number
  sort_order: number
  content_field_count: number
  children?: Competency[]
}

interface ContentField {
  id: number
  name: string
  description?: string
  content_type: string
  difficulty_level: string
  estimated_duration?: number
  materials_needed?: string
  learning_objectives?: string
  assessment_criteria?: string
  sort_order: number
}

interface CompetencyContentMapping {
  id: number
  competency_id: number
  content_field_id: number
  relationship_type: string
  strength: number
  description?: string
  competency_name: string
  competency_description?: string
  content_field_name: string
  content_field_description?: string
  content_type: string
  sort_order: number
}

interface Curriculum {
  id: number
  cid?: string
  name: string
  subject: string
  exam_type?: string
  competencies: Competency[]
}

interface CompetencyNodeProps {
  competency: Competency
  level: number
  onEdit: (competency: Competency) => void
  onDelete: (id: number) => void
  onAddChild: (parentId: number) => void
  expandedNodes: Set<number>
  onToggleExpanded: (id: number) => void
  onReorder: (parentId: number | null, competencyIds: number[]) => void
  onMove: (competencyId: number, newParentId: number | null) => void
}

function CompetencyNode({ competency, level, onEdit, onDelete, onAddChild, expandedNodes, onToggleExpanded, onReorder, onMove }: CompetencyNodeProps) {
  const expanded = expandedNodes.has(competency.id)
  const hasChildren = competency.children && competency.children.length > 0

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: competency.id,
    data: {
      type: 'competency',
      competency,
    }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'bg-gray-100 rounded' : ''}>
      <div className="flex items-center gap-2 py-1">
        <button
          {...attributes}
          {...listeners}
          className="p-1 hover:bg-gray-200 rounded cursor-grab active:cursor-grabbing"
          title="Ziehen zum Verschieben"
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </button>
        
        {hasChildren ? (
          <button
            onClick={() => onToggleExpanded(competency.id)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <div className="w-6" />
        )}
        
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900">{competency.name}</div>
          {competency.description && (
            <div className="text-sm text-gray-500 truncate">{competency.description}</div>
          )}
        </div>
        
        <div className="flex gap-1">
          <button
            onClick={() => onAddChild(competency.id)}
            className="p-1 text-green-600 hover:bg-green-50 rounded"
            title="Unterkompetenz hinzufügen"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(competency)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
            title="Bearbeiten"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(competency.id)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
            title="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {expanded && hasChildren && (
        <div className="ml-6">
          <SortableContext items={competency.children!.map(child => child.id)} strategy={verticalListSortingStrategy}>
            {competency.children!.map(child => (
              <CompetencyNode
                key={child.id}
                competency={child}
                level={level + 1}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddChild={onAddChild}
                expandedNodes={expandedNodes}
                onToggleExpanded={onToggleExpanded}
                onReorder={onReorder}
                onMove={onMove}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  )
}

// Sortable Content Field Component
interface SortableContentFieldProps {
  contentField: ContentField
  onEdit: () => void
  onDelete: (id: number) => void
}

function SortableContentField({ contentField, onEdit, onDelete }: SortableContentFieldProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: contentField.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'bg-gray-100 rounded' : ''}>
      <div className="border rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2 flex-1">
            <button
              {...attributes}
              {...listeners}
              className="p-1 hover:bg-gray-200 rounded cursor-grab active:cursor-grabbing"
              title="Ziehen zum Verschieben"
            >
              <GripVertical className="w-4 h-4 text-gray-400" />
            </button>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">{contentField.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{contentField.description}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className="capitalize">{contentField.content_type}</span>
                <span className="capitalize">{contentField.difficulty_level}</span>
                {contentField.estimated_duration && (
                  <span>{contentField.estimated_duration} min</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={onEdit}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="Bearbeiten"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(contentField.id)}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Löschen"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Sortable Mapping Component
interface SortableMappingProps {
  mapping: CompetencyContentMapping
  onEdit: (mapping: CompetencyContentMapping) => void
  onDelete: (id: number) => void
}

function SortableMapping({ mapping, onEdit, onDelete }: SortableMappingProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mapping.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'bg-gray-100 rounded' : ''}>
      <div className="border rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2 flex-1">
            <button
              {...attributes}
              {...listeners}
              className="p-1 hover:bg-gray-200 rounded cursor-grab active:cursor-grabbing"
              title="Ziehen zum Verschieben"
            >
              <GripVertical className="w-4 h-4 text-gray-400" />
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <div>
                  <h3 className="font-medium text-gray-900">{mapping.competency_name}</h3>
                  <p className="text-sm text-gray-600">{mapping.competency_description}</p>
                </div>
                <div className="text-gray-400">→</div>
                <div>
                  <h3 className="font-medium text-gray-900">{mapping.content_field_name}</h3>
                  <p className="text-sm text-gray-600">{mapping.content_field_description}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className="capitalize">{mapping.relationship_type}</span>
                <span>Stärke: {(mapping.strength * 100).toFixed(0)}%</span>
                <span className="capitalize">{mapping.content_type}</span>
              </div>
              {mapping.description && (
                <p className="text-sm text-gray-600 mt-2">{mapping.description}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onEdit(mapping)}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="Bearbeiten"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(mapping.id)}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Löschen"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function CurriculumEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [curriculum, setCurriculum] = useState<Curriculum | null>(null)
  const [loading, setLoading] = useState(true)
  const [editingCompetency, setEditingCompetency] = useState<Competency | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newCompetencyParent, setNewCompetencyParent] = useState<number | null>(null)
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set())
  const [isSaving, setIsSaving] = useState(false)
  const [isReordering, setIsReordering] = useState(false)
  
  // Content fields state
  const [contentFields, setContentFields] = useState<ContentField[]>([])
  const [mappings, setMappings] = useState<CompetencyContentMapping[]>([])
  const [activeTab, setActiveTab] = useState<'competencies' | 'content-fields' | 'mappings' | 'unified'>('competencies')
  
  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [parentId, setParentId] = useState<number | null>(null)
  
  // Content field form state
  const [contentFieldName, setContentFieldName] = useState('')
  const [contentFieldDescription, setContentFieldDescription] = useState('')
  const [contentType, setContentType] = useState('practical')
  const [difficultyLevel, setDifficultyLevel] = useState('intermediate')
  const [estimatedDuration, setEstimatedDuration] = useState<number | undefined>()
  const [materialsNeeded, setMaterialsNeeded] = useState('')
  const [learningObjectives, setLearningObjectives] = useState('')
  const [assessmentCriteria, setAssessmentCriteria] = useState('')
  
  // Mapping form state
  const [editingMapping, setEditingMapping] = useState<CompetencyContentMapping | null>(null)
  const [mappingCompetencyId, setMappingCompetencyId] = useState<number>(0)
  const [mappingContentFieldId, setMappingContentFieldId] = useState<number>(0)
  const [mappingRelationshipType, setMappingRelationshipType] = useState('direct')
  const [mappingStrength, setMappingStrength] = useState(1.0)
  const [mappingDescription, setMappingDescription] = useState('')
  
  // Unified Editor State
  const [showUnifiedEditor, setShowUnifiedEditor] = useState(false)
  const [unifiedEditorTab, setUnifiedEditorTab] = useState<'competencies' | 'content-fields' | 'mappings'>('competencies')
  
  // Print State
  const [showPrintView, setShowPrintView] = useState(false)

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const loadCurriculum = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/curricula/${id}/tree`)
      setCurriculum(response.data.data)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Laden des Curriculums')
      navigate('/curricula')
    } finally {
      setLoading(false)
    }
  }

  const loadContentFields = async () => {
    try {
      const response = await api.get(`/curricula/${id}/content-fields`)
      setContentFields(response.data.data)
    } catch (error: any) {
      console.error('Error loading content fields:', error)
    }
  }

  const loadMappings = async () => {
    try {
      const response = await api.get(`/curricula/${id}/mappings`)
      setMappings(response.data.data)
    } catch (error: any) {
      console.error('Error loading mappings:', error)
    }
  }

  const loadAllData = async () => {
    await Promise.all([
      loadCurriculum(),
      loadContentFields(),
      loadMappings()
    ])
  }

  useEffect(() => {
    if (id) {
      loadAllData()
    }
  }, [id])

  const flattenCompetencies = (competencies: Competency[]): Competency[] => {
    const result: Competency[] = []
    competencies.forEach(comp => {
      result.push(comp)
      if (comp.children) {
        result.push(...flattenCompetencies(comp.children))
      }
    })
    return result
  }

  const handleEdit = (competency: Competency) => {
    setEditingCompetency(competency)
    setName(competency.name)
    setDescription(competency.description || '')
    setParentId(competency.parent_id || null)
    setShowAddForm(false)
  }

  const handleAddChild = (parentId: number) => {
    setNewCompetencyParent(parentId)
    setName('')
    setDescription('')
    setParentId(parentId)
    setEditingCompetency(null)
    setShowAddForm(true)
  }

  const handleAddRoot = () => {
    setNewCompetencyParent(null)
    setName('')
    setDescription('')
    setParentId(null)
    setEditingCompetency(null)
    setShowAddForm(true)
  }

  const handleSave = async () => {
    if (isSaving) return // Prevent double submission
    
    try {
      setIsSaving(true)
      
      if (editingCompetency) {
        // Update existing competency
        await api.put(`/curricula/competencies/${editingCompetency.id}`, {
          name,
          description,
          parent_id: parentId
        })
        toast.success('Kompetenz aktualisiert')
      } else {
        // Create new competency
        await api.post('/curricula/competencies', {
          curriculum_id: curriculum!.id,
          name,
          description,
          parent_id: parentId
        })
        toast.success('Kompetenz erstellt')
        
        // If we added a child competency, expand the parent
        if (parentId) {
          setExpandedNodes(prev => new Set([...prev, parentId]))
        }
      }
      
      setEditingCompetency(null)
      setShowAddForm(false)
      await loadCurriculum()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Speichern')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleExpanded = (nodeId: number) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }

  const handleCancel = () => {
    setEditingCompetency(null)
    setShowAddForm(false)
    setName('')
    setDescription('')
    setParentId(null)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const activeCompetency = active.data.current?.competency
    const overCompetency = over.data.current?.competency

    if (!activeCompetency || !overCompetency) return

    // Check if we're moving within the same parent or to a different parent
    if (activeCompetency.parent_id === overCompetency.parent_id) {
      // Same parent - just reorder
      await handleReorder(activeCompetency.parent_id, Number(active.id), Number(over.id))
    } else {
      // Different parent - move to new parent
      await handleMove(Number(active.id), overCompetency.parent_id)
    }
  }

  const handleReorderWrapper = (parentId: number | null, competencyIds: number[]) => {
    // This is a wrapper for the drag-and-drop reorder functionality
    // The actual reordering is handled by handleDragEnd
  }

  const handleReorder = async (parentId: number | null, activeId: number, overId: number) => {
    if (!curriculum) return

    try {
      setIsReordering(true)
      
      // Get all competencies for this parent
      const allCompetencies = flattenCompetencies(curriculum.competencies)
      const parentCompetencies = allCompetencies.filter(comp => comp.parent_id === parentId)
      
      const oldIndex = parentCompetencies.findIndex(comp => comp.id === activeId)
      const newIndex = parentCompetencies.findIndex(comp => comp.id === overId)
      
      if (oldIndex === -1 || newIndex === -1) return
      
      const reorderedCompetencies = arrayMove(parentCompetencies, oldIndex, newIndex)
      const competencyIds = reorderedCompetencies.map(comp => comp.id)
      
      await api.put('/curricula/competencies/reorder', { 
        competencyIds,
        parentId 
      })
      
      toast.success('Reihenfolge aktualisiert')
      await loadCurriculum()
    } catch (error: any) {
      console.error('Reorder error:', error)
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Bitte melden Sie sich erneut an')
        window.location.reload()
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error || 'Ungültige Anfrage'
        toast.error(`Fehler: ${errorMessage}`)
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Unbekannter Fehler'
        toast.error(`Fehler beim Aktualisieren der Reihenfolge: ${errorMessage}`)
      }
    } finally {
      setIsReordering(false)
    }
  }

  const handleMove = async (competencyId: number, newParentId: number | null) => {
    if (!curriculum) return

    try {
      setIsReordering(true)
      
      await api.put(`/curricula/competencies/${competencyId}/move`, {
        parent_id: newParentId,
        curriculum_id: curriculum.id
      })
      
      toast.success('Kompetenz verschoben')
      await loadCurriculum()
    } catch (error: any) {
      console.error('Move error:', error)
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Bitte melden Sie sich erneut an')
        window.location.reload()
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error || 'Ungültige Anfrage'
        toast.error(`Fehler: ${errorMessage}`)
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Unbekannter Fehler'
        toast.error(`Fehler beim Verschieben der Kompetenz: ${errorMessage}`)
      }
    } finally {
      setIsReordering(false)
    }
  }

  // Content field handlers
  const handleCreateContentField = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!contentFieldName.trim()) return

    try {
      setIsSaving(true)
      await api.post(`/curricula/${id}/content-fields`, {
        name: contentFieldName,
        description: contentFieldDescription,
        content_type: contentType,
        difficulty_level: difficultyLevel,
        estimated_duration: estimatedDuration,
        materials_needed: materialsNeeded,
        learning_objectives: learningObjectives,
        assessment_criteria: assessmentCriteria
      })
      
      toast.success('Inhaltsfeld erstellt')
      setContentFieldName('')
      setContentFieldDescription('')
      setContentType('practical')
      setDifficultyLevel('intermediate')
      setEstimatedDuration(undefined)
      setMaterialsNeeded('')
      setLearningObjectives('')
      setAssessmentCriteria('')
      await loadContentFields()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Erstellen des Inhaltsfelds')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteContentField = async (contentFieldId: number) => {
    if (!confirm('Möchten Sie dieses Inhaltsfeld wirklich löschen?')) return

    try {
      await api.delete(`/curricula/content-fields/${contentFieldId}`)
      toast.success('Inhaltsfeld gelöscht')
      await loadContentFields()
      await loadMappings()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen des Inhaltsfelds')
    }
  }

  const handleCreateMapping = async (e: React.FormEvent) => {
    e.preventDefault()
    if (mappingCompetencyId === 0 || mappingContentFieldId === 0) {
      toast.error('Bitte wählen Sie eine Kompetenz und ein Inhaltsfeld aus')
      return
    }

    try {
      setIsSaving(true)
      await api.post('/curricula/mappings', {
        competency_id: mappingCompetencyId,
        content_field_id: mappingContentFieldId,
        relationship_type: mappingRelationshipType,
        strength: mappingStrength,
        description: mappingDescription
      })
      toast.success('Mapping erstellt')
      setMappingCompetencyId(0)
      setMappingContentFieldId(0)
      setMappingRelationshipType('direct')
      setMappingStrength(1.0)
      setMappingDescription('')
      await loadMappings()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Erstellen des Mappings')
    } finally {
      setIsSaving(false)
    }
  }

  const handleEditMapping = (mapping: CompetencyContentMapping) => {
    setEditingMapping(mapping)
    setMappingCompetencyId(mapping.competency_id)
    setMappingContentFieldId(mapping.content_field_id)
    setMappingRelationshipType(mapping.relationship_type)
    setMappingStrength(mapping.strength)
    setMappingDescription(mapping.description || '')
  }

  const handleUpdateMapping = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingMapping) return

    try {
      setIsSaving(true)
      await api.put(`/curricula/mappings/${editingMapping.id}`, {
        competency_id: mappingCompetencyId,
        content_field_id: mappingContentFieldId,
        relationship_type: mappingRelationshipType,
        strength: mappingStrength,
        description: mappingDescription
      })
      
      toast.success('Mapping aktualisiert')
      setEditingMapping(null)
      setMappingCompetencyId(0)
      setMappingContentFieldId(0)
      setMappingRelationshipType('direct')
      setMappingStrength(1.0)
      setMappingDescription('')
      await loadMappings()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Aktualisieren des Mappings')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteMapping = async (mappingId: number) => {
    if (!confirm('Möchten Sie dieses Mapping wirklich löschen?')) return

    try {
      await api.delete(`/curricula/mappings/${mappingId}`)
      toast.success('Mapping gelöscht')
      await loadMappings()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen des Mappings')
    }
  }

  const handleDelete = async (competencyId: number) => {
    if (!confirm('Kompetenz wirklich löschen?')) return
    
    try {
      await api.delete(`/curricula/competencies/${competencyId}`)
      toast.success('Kompetenz gelöscht')
      await loadCurriculum()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen')
    }
  }

  // Content Fields Drag & Drop Handler
  const handleContentFieldsDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = contentFields.findIndex(field => field.id === active.id)
    const newIndex = contentFields.findIndex(field => field.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    const reorderedFields = arrayMove(contentFields, oldIndex, newIndex)
    setContentFields(reorderedFields)

    try {
      setIsReordering(true)
      const fieldIds = reorderedFields.map(field => field.id)
      
      console.log('🔄 ===== FRONTEND CONTENT FIELDS REORDER =====');
      console.log('📋 Original contentFields:', contentFields.map(f => ({ id: f.id, name: f.name })));
      console.log('📋 Reordered fields:', reorderedFields.map(f => ({ id: f.id, name: f.name })));
      console.log('📋 fieldIds to send:', fieldIds);
      console.log('📋 curriculum_id:', curriculum!.id);
      console.log('📋 fieldIds type:', typeof fieldIds);
      console.log('📋 fieldIds is array:', Array.isArray(fieldIds));
      console.log('📋 fieldIds length:', fieldIds.length);
      
      // Ensure fieldIds is a proper array
      const fieldIdsArray = Array.isArray(fieldIds) ? fieldIds : Object.values(fieldIds);
      
      const requestData = { 
        fieldIds: fieldIdsArray,
        curriculum_id: curriculum!.id 
      };
      
      console.log('📋 Request data:', JSON.stringify(requestData, null, 2));
      console.log('🔄 Sending API request...');
      
      const response = await api.put('/curricula/content-fields/reorder', requestData);
      
      console.log('✅ API response received:', response.data);
      console.log('🔄 ===== END FRONTEND REORDER =====');
      
      toast.success('Reihenfolge aktualisiert')
    } catch (error: any) {
      console.error('❌ ===== FRONTEND REORDER ERROR =====');
      console.error('❌ Content fields reorder error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers
      });
      console.error('❌ ===== END FRONTEND ERROR =====');
      
      toast.error(`Fehler beim Aktualisieren der Reihenfolge: ${error.response?.data?.error || error.message}`)
      await loadContentFields()
    } finally {
      setIsReordering(false)
    }
  }

  // Mappings Drag & Drop Handler
  const handleMappingsDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = mappings.findIndex(mapping => mapping.id === active.id)
    const newIndex = mappings.findIndex(mapping => mapping.id === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    const reorderedMappings = arrayMove(mappings, oldIndex, newIndex)
    setMappings(reorderedMappings)

    try {
      setIsReordering(true)
      const mappingIds = reorderedMappings.map(mapping => mapping.id)
      await api.put('/curricula/mappings/reorder', { 
        mappingIds,
        curriculum_id: curriculum!.id 
      })
      toast.success('Reihenfolge aktualisiert')
    } catch (error: any) {
      console.error('Mappings reorder error:', error)
      toast.error('Fehler beim Aktualisieren der Reihenfolge')
      await loadMappings()
    } finally {
      setIsReordering(false)
    }
  }

  // Unified Editor Handlers
  const handleToggleUnifiedEditor = () => {
    setShowUnifiedEditor(!showUnifiedEditor)
  }

  const handleUnifiedEditorSave = () => {
    // Curriculum-Daten neu laden nach Änderungen
    loadCurriculum()
  }

  // PDF Generation Handler
  const handleGeneratePDF = async () => {
    if (!curriculum) return;
    
    try {
      const filename = sanitizeFilename(curriculum.name || 'curriculum');
      await generatePDF('curriculum-print-view-editor', { filename });
      toast.success('PDF erfolgreich erstellt!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Fehler beim Erstellen der PDF');
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!curriculum) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Curriculum nicht gefunden</div>
      </div>
    )
  }

  const allCompetencies = flattenCompetencies(curriculum.competencies)

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{curriculum.name}</h1>
            <p className="text-gray-600">{curriculum.subject} {curriculum.exam_type && `• ${curriculum.exam_type}`}</p>
          </div>
          <HelpButton helpKey="curricula.editor.overview" />
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowPrintView(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            title="Druckansicht öffnen"
          >
            <Printer className="w-4 h-4" />
            Drucken
          </button>
          <button
            onClick={() => navigate('/curricula')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            ← Zurück
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('competencies')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'competencies'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Kompetenzen ({curriculum.competencies.length})
              <HelpButton helpKey="curricula.editor.competencies" size="sm" />
            </button>
            <button
              onClick={() => setActiveTab('content-fields')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'content-fields'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Inhaltsfelder ({contentFields.length})
              <HelpButton helpKey="curricula.editor.content_fields" size="sm" />
            </button>
            <button
              onClick={() => setActiveTab('mappings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'mappings'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Mappings ({mappings.length})
              <HelpButton helpKey="curricula.editor.mappings" size="sm" />
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'competencies' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tree View */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium">Kompetenzen</h2>
              <button
                onClick={handleAddRoot}
                className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                Hinzufügen
              </button>
            </div>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={curriculum.competencies.map(comp => comp.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {curriculum.competencies.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">
                    Noch keine Kompetenzen vorhanden
                  </div>
                ) : (
                  curriculum.competencies.map(competency => (
                    <CompetencyNode
                      key={competency.id}
                      competency={competency}
                      level={0}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onAddChild={handleAddChild}
                      expandedNodes={expandedNodes}
                      onToggleExpanded={handleToggleExpanded}
                      onReorder={handleReorderWrapper}
                      onMove={handleMove}
                    />
                  ))
                )}
              </div>
            </SortableContext>
          </DndContext>
          {isReordering && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
              <div className="text-sm text-gray-600">Aktualisiere...</div>
            </div>
          )}
        </div>

        {/* Edit Form */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">
            {editingCompetency ? 'Kompetenz bearbeiten' : showAddForm ? 'Neue Kompetenz' : 'Kompetenz auswählen'}
          </h2>
          
          {(editingCompetency || showAddForm) ? (
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Übergeordnete Kompetenz
                </label>
                <select
                  value={parentId || ''}
                  onChange={(e) => setParentId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">(keine)</option>
                  {allCompetencies
                    .filter(comp => !editingCompetency || comp.id !== editingCompetency.id)
                    .map(comp => (
                      <option key={comp.id} value={comp.id}>
                        {comp.name}
                      </option>
                    ))}
                </select>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Speichere...' : 'Speichern'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  <X className="w-4 h-4" />
                  Abbrechen
                </button>
              </div>
            </form>
          ) : (
            <div className="text-gray-500 text-center py-8">
              Wählen Sie eine Kompetenz aus, um sie zu bearbeiten
            </div>
          )}
        </div>
        </div>
      )}

      {activeTab === 'content-fields' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Content Fields List with Drag & Drop */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium">Inhaltsfelder</h2>
              <button
                onClick={() => {
                  setContentFieldName('')
                  setContentFieldDescription('')
                  setContentType('practical')
                  setDifficultyLevel('intermediate')
                  setEstimatedDuration(undefined)
                  setMaterialsNeeded('')
                  setLearningObjectives('')
                  setAssessmentCriteria('')
                }}
                className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                Hinzufügen
              </button>
            </div>
            
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleContentFieldsDragEnd}
            >
              <SortableContext items={contentFields.map(field => field.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {contentFields.length === 0 ? (
                    <div className="text-gray-500 text-center py-8">
                      Noch keine Inhaltsfelder vorhanden
                    </div>
                  ) : (
                    contentFields.map(contentField => (
                      <SortableContentField
                        key={contentField.id}
                        contentField={contentField}
                        onEdit={() => {
                          setContentFieldName(contentField.name)
                          setContentFieldDescription(contentField.description || '')
                          setContentType(contentField.content_type)
                          setDifficultyLevel(contentField.difficulty_level)
                          setEstimatedDuration(contentField.estimated_duration)
                          setMaterialsNeeded(contentField.materials_needed || '')
                          setLearningObjectives(contentField.learning_objectives || '')
                          setAssessmentCriteria(contentField.assessment_criteria || '')
                        }}
                        onDelete={handleDeleteContentField}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Add Content Field Form */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium mb-4">Inhaltsfeld hinzufügen</h2>
            
            <form onSubmit={handleCreateContentField} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={contentFieldName}
                  onChange={(e) => setContentFieldName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={contentFieldDescription}
                  onChange={(e) => setContentFieldDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Typ
                  </label>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="practical">Praktisch</option>
                    <option value="theoretical">Theoretisch</option>
                    <option value="assessment">Bewertung</option>
                    <option value="project">Projekt</option>
                    <option value="experiment">Experiment</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Schwierigkeit
                  </label>
                  <select
                    value={difficultyLevel}
                    onChange={(e) => setDifficultyLevel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="beginner">Anfänger</option>
                    <option value="intermediate">Mittel</option>
                    <option value="advanced">Fortgeschritten</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Geschätzte Dauer (Minuten)
                </label>
                <input
                  type="number"
                  value={estimatedDuration || ''}
                  onChange={(e) => setEstimatedDuration(e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Benötigte Materialien
                </label>
                <textarea
                  value={materialsNeeded}
                  onChange={(e) => setMaterialsNeeded(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lernziele
                </label>
                <textarea
                  value={learningObjectives}
                  onChange={(e) => setLearningObjectives(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bewertungskriterien
                </label>
                <textarea
                  value={assessmentCriteria}
                  onChange={(e) => setAssessmentCriteria(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <button
                type="submit"
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {isSaving ? 'Erstelle...' : 'Inhaltsfeld erstellen'}
              </button>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'mappings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mappings List */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium">Kompetenz-Inhaltsfeld Mappings</h2>
              <button
                onClick={() => {
                  setEditingMapping(null)
                  setMappingCompetencyId(0)
                  setMappingContentFieldId(0)
                  setMappingRelationshipType('direct')
                  setMappingStrength(1.0)
                  setMappingDescription('')
                }}
                className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                Mapping hinzufügen
              </button>
            </div>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleMappingsDragEnd}
          >
            <SortableContext items={mappings.map(mapping => mapping.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {mappings.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">
                    Noch keine Mappings vorhanden
                  </div>
                ) : (
                  mappings.map(mapping => (
                      <SortableMapping
                        key={mapping.id}
                        mapping={mapping}
                        onEdit={handleEditMapping}
                        onDelete={handleDeleteMapping}
                      />
                  ))
                )}
              </div>
            </SortableContext>
          </DndContext>
          </div>

          {/* Mapping Edit Form */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium mb-4">
              {editingMapping ? 'Mapping bearbeiten' : 'Neues Mapping erstellen'}
            </h2>
            
            <form onSubmit={editingMapping ? handleUpdateMapping : handleCreateMapping} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kompetenz *
                  </label>
                  <select
                    value={mappingCompetencyId}
                    onChange={(e) => setMappingCompetencyId(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value={0}>Kompetenz auswählen</option>
                    {curriculum?.competencies && curriculum.competencies.map((comp) => (
                      <option key={comp.id} value={comp.id}>
                        {comp.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inhaltsfeld *
                  </label>
                  <select
                    value={mappingContentFieldId}
                    onChange={(e) => setMappingContentFieldId(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value={0}>Inhaltsfeld auswählen</option>
                    {contentFields.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beziehungstyp
                  </label>
                  <select
                    value={mappingRelationshipType}
                    onChange={(e) => setMappingRelationshipType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="direct">Direkt</option>
                    <option value="supporting">Unterstützend</option>
                    <option value="prerequisite">Voraussetzung</option>
                    <option value="assessment">Bewertung</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stärke (0.0 - 1.0)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={mappingStrength}
                    onChange={(e) => setMappingStrength(parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <div className="mt-1">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${mappingStrength * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {Math.round(mappingStrength * 100)}% Stärke
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschreibung der Beziehung
                </label>
                <textarea
                  value={mappingDescription}
                  onChange={(e) => setMappingDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Beschreibung der Beziehung zwischen Kompetenz und Inhaltsfeld"
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSaving ? 'Speichere...' : (editingMapping ? 'Aktualisieren' : 'Erstellen')}
                </button>
                {editingMapping && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingMapping(null)
                      setMappingCompetencyId(0)
                      setMappingContentFieldId(0)
                      setMappingRelationshipType('direct')
                      setMappingStrength(1.0)
                      setMappingDescription('')
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {showPrintView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 no-print">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                PDF Vorschau: {curriculum.name}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleGeneratePDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  PDF erstellen
                </button>
                <button
                  onClick={() => setShowPrintView(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto">
              <div id="curriculum-print-view-editor">
                <CurriculumPrintView
                  curriculum={curriculum}
                  competencies={curriculum.competencies}
                  contentFields={contentFields}
                  mappings={mappings}
                />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
